// Live visit tracking hook — captures GPS via the browser's Geolocation API,
// pings the backend every ~30s while a visit is active, and surfaces geofence
// status so the mobile UI can prompt the provider when they wander off site.
import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const PING_INTERVAL_MS = 5000;  // 5s — updates dispatchers as close to real-time as watchPosition allows
const REFRESH_STATE_MS = 20000; // occasional re-sync of dispatcher-visible fields

export function useVisitTracking() {
  const [session, setSession] = useState(null);         // active tracking session (or null)
  const [outside, setOutside] = useState(false);        // provider is >200m from visit site
  const [distanceM, setDistanceM] = useState(0);
  const [error, setError] = useState(null);             // last GPS or API error message
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const watchIdRef = useRef(null);
  const pingTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const lastPingAtRef = useRef(0);
  const lastCoordsRef = useRef(null);
  const visitIdRef = useRef(null);

  // Prompt & resolve the current GPS position once (used at Start).
  const capturePosition = useCallback(() => new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(new Error("Geolocation is not supported in this browser."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy_m: pos.coords.accuracy,
      }),
      (err) => reject(new Error(err.message || "Unable to read your location.")),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 },
    );
  }), []);

  const stopWatch = useCallback(() => {
    if (watchIdRef.current != null && "geolocation" in navigator) {
      try { navigator.geolocation.clearWatch(watchIdRef.current); } catch {}
    }
    watchIdRef.current = null;
    if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
    if (refreshTimerRef.current) { clearInterval(refreshTimerRef.current); refreshTimerRef.current = null; }
    lastCoordsRef.current = null;
    lastPingAtRef.current = 0;
  }, []);

  const sendPing = useCallback(async (coords) => {
    if (!visitIdRef.current || !coords) return;
    try {
      const r = await api.post("/visit-tracking/ping", {
        visit_id: visitIdRef.current,
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy_m: coords.accuracy_m,
      });
      setSession((prev) => ({ ...(prev || {}), ...(r.data || {}) }));
      setOutside(!!r.data.outside_geofence);
      setDistanceM(r.data.distance_m || 0);
      setError(null);
    } catch (e) {
      // Session may have ended server-side; stop the watcher gracefully.
      const msg = formatApiError(e);
      setError(msg);
      if (e?.response?.status === 404) stopWatch();
    }
  }, [stopWatch]);

  // watchPosition callback: keep latest coords cached, ping at PING_INTERVAL_MS
  const startWatch = useCallback(() => {
    if (!("geolocation" in navigator)) return;
    stopWatch();
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        lastCoordsRef.current = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
        };
        const now = Date.now();
        // Send an immediate ping if enough time has elapsed
        if (now - lastPingAtRef.current >= PING_INTERVAL_MS) {
          lastPingAtRef.current = now;
          sendPing(lastCoordsRef.current);
        }
      },
      (err) => setError(err.message || "GPS unavailable"),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 },
    );

    // Fallback timer — guarantees a ping every PING_INTERVAL_MS even if the OS
    // is throttling watchPosition when the screen is locked.
    pingTimerRef.current = setInterval(() => {
      if (lastCoordsRef.current) sendPing(lastCoordsRef.current);
    }, PING_INTERVAL_MS);
  }, [sendPing, stopWatch]);

  const startTracking = useCallback(async ({ visitId, kind = "fleet", label,
                                              siteLatitude, siteLongitude }) => {
    if (!visitId) throw new Error("visitId is required");
    setStarting(true);
    setError(null);
    try {
      // Always prompt for the provider's current GPS so we (a) verify
      // permission before moving the visit forward, and (b) seed the first
      // breadcrumb from a real location. The geofence anchor is either the
      // caller-supplied patient site coords or, as a fallback, the provider's
      // current coords.
      const coords = await capturePosition();
      const anchorLat = (typeof siteLatitude === "number") ? siteLatitude : coords.latitude;
      const anchorLng = (typeof siteLongitude === "number") ? siteLongitude : coords.longitude;
      const r = await api.post("/visit-tracking/start", {
        visit_id: visitId,
        kind,
        site_latitude: anchorLat,
        site_longitude: anchorLng,
        label,
      });
      visitIdRef.current = visitId;
      setSession(r.data);
      setOutside(false);
      setDistanceM(0);
      lastCoordsRef.current = coords;
      lastPingAtRef.current = Date.now();
      startWatch();
      // Fire an immediate ping with the real GPS so the dispatcher sees the
      // provider at their true location (not the anchor) right away.
      sendPing(coords);
      return r.data;
    } catch (e) {
      const msg = e?.response ? formatApiError(e) : (e.message || "Failed to start tracking");
      setError(msg);
      throw new Error(msg);
    } finally {
      setStarting(false);
    }
  }, [capturePosition, sendPing, startWatch]);

  const endTracking = useCallback(async () => {
    if (!visitIdRef.current) return;
    setEnding(true);
    try {
      await api.post("/visit-tracking/end", { visit_id: visitIdRef.current });
      stopWatch();
      setSession(null);
      setOutside(false);
      visitIdRef.current = null;
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setEnding(false);
    }
  }, [stopWatch]);

  // Restore an existing active session for the given visit (used when a
  // provider reloads the page mid-visit).
  const resumeIfActive = useCallback(async (visitId) => {
    if (!visitId) return null;
    try {
      const r = await api.get(`/visit-tracking/session/${visitId}`);
      const s = r.data?.session;
      if (s && !s.ended_at) {
        visitIdRef.current = visitId;
        setSession(s);
        setOutside(!!s.outside_geofence_since);
        setDistanceM(s.last_distance_m || 0);
        startWatch();
        return s;
      }
    } catch { /* ignore — no session */ }
    return null;
  }, [startWatch]);

  // Cleanup on unmount
  useEffect(() => () => stopWatch(), [stopWatch]);

  return {
    session, outside, distanceM, error,
    starting, ending,
    startTracking, endTracking, resumeIfActive,
    isTracking: !!session && !session.ended_at,
  };
}
